import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { plataforma } from '@/platform';
import { avaliacoesApi, type NotaOS } from '@/features/pos-venda/api/avaliacoesApi';

/**
 * Acima disto a lista de ids deixaria a URL grande demais (UUIDs de 36
 * caracteres); pede todas as respondidas da conta e filtra aqui.
 */
const MAX_IDS_NA_URL = 100;
/** Remontar a tela (abrir uma O.S. e voltar) dentro disto não busca de novo. */
const VALIDADE_CACHE_MS = 30_000;

type Cache = { chave: string; em: number; notas: NotaOS[] };
let cache: Cache | null = null;

/** Só para testes: esquece o cache entre um caso e outro. */
export function limparCacheNotasAvaliacao(): void {
  cache = null;
}

const MAPA_VAZIO: ReadonlyMap<string, NotaOS> = new Map();

/**
 * Notas das avaliações respondidas das O.S. visíveis (cards do kanban),
 * numa chamada só. Recarrega quando o cliente responde
 * (`aoAvaliacaoPosVenda`) e quando a janela volta ao foco.
 *
 * É enfeite do card: qualquer falha (offline, servidor fora, rota ainda
 * não publicada) vira mapa vazio, sem erro — o kanban nunca quebra por isso.
 */
export function useNotasAvaliacao(ids: readonly string[]): ReadonlyMap<string, NotaOS> {
  const chave = useMemo(() => {
    if (ids.length === 0) return '';
    if (ids.length > MAX_IDS_NA_URL) return '*';
    return [...new Set(ids)].sort().join(',');
  }, [ids]);

  const [notas, setNotas] = useState<NotaOS[]>(() =>
    cache && cache.chave === chave ? cache.notas : [],
  );
  const chaveAtual = useRef(chave);
  chaveAtual.current = chave;

  const carregar = useCallback(
    async (forcar: boolean) => {
      if (!chave) {
        setNotas([]);
        return;
      }
      if (!forcar && cache && cache.chave === chave && Date.now() - cache.em < VALIDADE_CACHE_MS) {
        setNotas(cache.notas);
        return;
      }
      try {
        const r = await avaliacoesApi.notas(chave === '*' ? undefined : chave.split(','));
        const lista = Array.isArray(r?.notas) ? r.notas : [];
        cache = { chave, em: Date.now(), notas: lista };
        if (chaveAtual.current === chave) setNotas(lista);
      } catch {
        // Silencioso de propósito: sem conexão, a estrela só não aparece.
        // Mantém o que já estava na tela.
      }
    },
    [chave],
  );

  useEffect(() => {
    void carregar(false);
  }, [carregar]);

  useEffect(() => plataforma.aoAvaliacaoPosVenda(() => void carregar(true)), [carregar]);

  useEffect(() => {
    const aoFocar = () => {
      if (document.visibilityState !== 'visible') return;
      // `focus` e `visibilitychange` chegam juntos: uma busca basta.
      if (cache && Date.now() - cache.em < 5_000) return;
      void carregar(true);
    };
    window.addEventListener('focus', aoFocar);
    document.addEventListener('visibilitychange', aoFocar);
    return () => {
      window.removeEventListener('focus', aoFocar);
      document.removeEventListener('visibilitychange', aoFocar);
    };
  }, [carregar]);

  return useMemo(() => {
    if (!notas.length) return MAPA_VAZIO;
    return new Map(notas.map((n) => [n.orcamentoId, n]));
  }, [notas]);
}
